import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { usersApi } from '../api/users';
import { ROLE_LABELS, Role } from '../types/user';
import LoadingScreen from '../components/LoadingScreen';

const AcceptInvitation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [inviteInfo, setInviteInfo] = useState<{ email: string; role: Role; companyName: string } | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) return;
    usersApi.getInvitationInfo(token)
      .then(setInviteInfo)
      .catch((err) => setError(err.response?.data?.message || 'Приглашение не найдено'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleGoogleLogin = async (credential: string) => {
    if (!token) return;
    setAccepting(true);
    try {
      const result = await usersApi.acceptInvitation(token, credential);
      loginWithToken(result.accessToken, result.user);
      // Backend guarantees needsCompany: false for accepted invitations
      navigate('/dashboard');
    } catch (err: any) {
      setAccepting(false);
      setError(err.response?.data?.message || 'Ошибка при принятии приглашения');
    }
  };

  if (loading) {
    return <LoadingScreen text="Загрузка приглашения..." />;
  }

  if (accepting) {
    return (
      <LoadingScreen
        text="Принимаем приглашение..."
        subtext="Выполняется вход в систему"
      />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Fin Calendar</h1>
          <div className="p-3 bg-red-100 text-red-800 rounded-md mb-4">{error}</div>
          <button
            onClick={() => navigate('/login')}
            className="text-blue-600 hover:underline"
          >
            Перейти к входу
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Fin Calendar</h1>
        <h2 className="text-lg text-gray-600 mb-6">Приглашение в компанию</h2>

        {inviteInfo && (
          <div className="mb-6 text-left bg-gray-50 p-4 rounded-md">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Компания:</span> {inviteInfo.companyName}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Email:</span> {inviteInfo.email}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Роль:</span> {ROLE_LABELS[inviteInfo.role]}
            </p>
          </div>
        )}

        <p className="text-gray-600 mb-4">
          Войдите через Google, чтобы принять приглашение
        </p>

        <div className="flex justify-center">
          <GoogleLogin
            onSuccess={(response) => {
              if (response.credential) {
                handleGoogleLogin(response.credential);
              }
            }}
            onError={() => setError('Ошибка авторизации Google')}
          />
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;
